import type { CalendarDate, Time } from "@internationalized/date";

import {
  getLocalTimeZone,
  toCalendarDateTime,
  today,
} from "@internationalized/date";
import dayjs from "dayjs";
import { CalendarDays, Clock, Globe } from "lucide-react";
import {
  I18nProvider,
  DatePicker as RACDatePicker,
  TimeField,
} from "react-aria-components";

import {
  DateInput,
  DatePickerButton,
  DatePickerCalendar,
  DatePickerGroup,
  DatePickerPopover,
} from "@/components/datepicker";
import { Label } from "@/components/field";

import { formatTimeZone } from "../schedule/formatTimeZone";

const FIELD_GROUP_CLASS = "h-11 gap-md py-0 shadow-xs";

export function ScheduleDateTime({
  date,
  onDateChange,
  onTimeChange,
  time,
}: {
  date: CalendarDate | null;
  onDateChange: (date: CalendarDate | null) => void;
  onTimeChange: (time: null | Time) => void;
  time: null | Time;
}) {
  const goesLiveAt =
    date && time
      ? toCalendarDateTime(date, time).toDate(getLocalTimeZone())
      : null;

  return (
    <div className="flex flex-col gap-lg">
      <I18nProvider locale="en-US">
        <div className="grid grid-cols-1 gap-lg sm:grid-cols-2">
          <RACDatePicker
            className="flex flex-col gap-sm"
            minValue={today(getLocalTimeZone())}
            onChange={onDateChange}
            value={date}
          >
            <Label>Date</Label>
            <DatePickerGroup className={FIELD_GROUP_CLASS}>
              <CalendarDays className="size-5 shrink-0 text-fg-quaternary" />
              <DateInput />
              <DatePickerButton />
            </DatePickerGroup>
            <DatePickerPopover>
              <DatePickerCalendar />
            </DatePickerPopover>
          </RACDatePicker>

          <TimeField
            className="flex flex-col gap-sm"
            onChange={onTimeChange}
            value={time}
          >
            <Label>Time</Label>
            <DatePickerGroup className={FIELD_GROUP_CLASS}>
              <Clock className="size-5 shrink-0 text-fg-quaternary" />
              <DateInput />
            </DatePickerGroup>
          </TimeField>
        </div>
      </I18nProvider>

      <p className="flex items-center gap-sm rounded-md bg-bg-secondary px-lg py-md text-sm text-text-tertiary">
        <Globe className="size-4 shrink-0 text-fg-quaternary" />
        {goesLiveAt ? (
          <span>
            Goes live{" "}
            <span className="font-medium text-text-secondary">
              {dayjs(goesLiveAt).format("dddd, MMM D [at] h:mm A")}
            </span>{" "}
            · {formatTimeZone()}
          </span>
        ) : (
          <span>Times are in {formatTimeZone()}, your account time zone.</span>
        )}
      </p>
    </div>
  );
}
